import { useState } from 'react';
import { Sparkles, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const CHANGELOG = [
  {
    version: '1.7.0',
    date: '2026-02-06',
    isNew: true,
    changes: [
      { type: 'feature', text: 'Asset Allocation Notifications - Email employees when assets are assigned' },
      { type: 'feature', text: 'One-click Accept/Reject allocation from email without login' },
      { type: 'feature', text: 'CRM Reporting Manager Notifications - Notify managers on lead status changes' },
      { type: 'improvement', text: 'Asset table now shows acceptance status (Pending/Accepted/Issue)' },
      { type: 'improvement', text: 'Configurable email templates for asset allocation' },
    ]
  },
  {
    version: '1.6.0',
    date: '2026-02-05',
    changes: [
      { type: 'feature', text: 'Petty Cash Management - Track daily office expenses with AI insights' },
      { type: 'feature', text: 'Email-based approvals - One-click approve/reject without login' },
      { type: 'feature', text: 'CRM Lost Deals tile - Track lost opportunities alongside won deals' },
      { type: 'improvement', text: 'Petty Cash configurable approval thresholds and categories' },
      { type: 'improvement', text: 'Weekly/Monthly reconciliation with AI-powered spending analysis' },
    ]
  },
  {
    version: '1.5.0',
    date: '2026-01-22',
    changes: [
      { type: 'feature', text: 'Purchase Orders with official letterhead PDF generation' },
      { type: 'feature', text: 'Invoice GST calculations (CGST, SGST, IGST)' },
      { type: 'feature', text: 'Performance Management - OKRs, Goals, Review Cycles' },
      { type: 'feature', text: 'Birthday & Anniversary celebration emails with templates' },
      { type: 'improvement', text: 'Standardized PDF letterhead across all documents' },
      { type: 'fix', text: 'Timesheet calendar date display fixed' },
      { type: 'fix', text: 'Payslip Date of Joining now shows correctly' },
    ]
  },
  {
    version: '1.4.0',
    date: '2025-01-08',
    changes: [
      { type: 'feature', text: 'Clickable dashboard stats - navigate directly to modules' },
      { type: 'feature', text: 'Asset Management - Full CRUD operations (Edit, Delete)' },
      { type: 'feature', text: 'Error logging system - Auto-capture and view in Admin panel' },
      { type: 'feature', text: 'Approval workflow email notifications' },
      { type: 'improvement', text: 'User Management shows all AD-synced employees' },
    ]
  },
  {
    version: '1.3.0',
    date: '2025-01-05',
    changes: [
      { type: 'feature', text: 'Active Directory (Azure AD) sync for employees' },
      { type: 'feature', text: 'Manager relationships auto-sync from AD' },
      { type: 'feature', text: 'Reporting manager field - view only for non-super admins' },
      { type: 'improvement', text: 'Codebase optimization - shared hooks and components' },
    ]
  },
  {
    version: '1.2.0',
    date: '2024-12-28',
    changes: [
      { type: 'feature', text: 'Travel Request module with multi-level approvals' },
      { type: 'feature', text: 'Timesheet management with DevOps integration' },
      { type: 'feature', text: 'Recruitment module - Requisitions, Candidates, Offers' },
      { type: 'feature', text: 'CRM module for lead management' },
    ]
  },
  {
    version: '1.1.0',
    date: '2024-12-20',
    changes: [
      { type: 'feature', text: 'Attendance tracking with check-in/check-out' },
      { type: 'feature', text: 'Leave management with approval workflow' },
      { type: 'feature', text: 'Expense claims with receipt upload' },
      { type: 'feature', text: 'Payroll calculations with deductions' },
    ]
  },
  {
    version: '1.0.0',
    date: '2024-12-15',
    changes: [
      { type: 'feature', text: 'Initial release of Vibe6 Hub' },
      { type: 'feature', text: 'Employee management' },
      { type: 'feature', text: 'User authentication with Microsoft SSO' },
      { type: 'feature', text: 'Role-based access control' },
    ]
  },
];

const typeColors = {
  feature: 'bg-green-100 text-green-800',
  improvement: 'bg-blue-100 text-blue-800',
  fix: 'bg-yellow-100 text-yellow-800',
  breaking: 'bg-red-100 text-red-800',
};

export default function ChangelogButton() {
  const [open, setOpen] = useState(false);
  const hasNew = CHANGELOG.some(v => v.isNew);

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(true)}
        className="relative"
        title="What's New"
      >
        <Sparkles className="h-5 w-5" />
        {hasNew && (
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              What&apos;s New in Vibe6 Hub
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {CHANGELOG.map((release) => (
              <div key={release.version} className="space-y-3">
                <div className="flex items-center gap-2">
                  <Badge variant={release.isNew ? 'default' : 'secondary'}>
                    v{release.version}
                  </Badge>
                  <span className="text-sm text-muted-foreground">{release.date}</span>
                  {release.isNew && (
                    <Badge className="bg-green-500 text-white">NEW</Badge>
                  )}
                </div>
                <ul className="space-y-2 pl-1">
                  {release.changes.map((change, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <ChevronRight className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                      <span>
                        <Badge variant="outline" className={`text-xs mr-2 ${typeColors[change.type]}`}>
                          {change.type}
                        </Badge>
                        {change.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
